import React, { useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import * as actions from "../../_redux/personnels/PersonnelsAction";
import {
  DeleteButton,
  ExportButton,
  FormButton,
  SuccessButton,
  copy,
  deletionAlert,
  exportToExcel,
  exportToPDF,
  getAuthorities,
  noRecordsAlert,
  print,
  redirectToPages,
  resultAlert
} from "../../../../../_helpers/Helpers";
import { 
  Card, 
  CardBody, 
  CardHeader,
  CardHeaderToolbar,
  ModalProgressBar
} from "../../../../../_metronic/_partials/controls";
import { PersonnelsTable } from "./personnels-table/PersonnelsTable";
import { PersonnelsFilter } from "./personnels-filter/PersonnelsFilter";
import { usePersonnelsUIContext } from "./PersonnelsUIContext";

export function PersonnelsCard() {
  // Personnels UI Context
  const personnelsUIContext = usePersonnelsUIContext();
  const personnelsUIProps = useMemo(() => {
    return {
      ids: personnelsUIContext.ids,
      setIds: personnelsUIContext.setIds,
      queryParams: personnelsUIContext.queryParams,
      openNewPersonnelPage: personnelsUIContext.openNewPersonnelPage,
    };
  }, [personnelsUIContext]);
    
  // Getting curret state of Personnel list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.personnels }),
    shallowEqual
  );
  const { actionsLoading, isSuccess, type, authorities } = currentState;
  
  // Authority Parameters
  const { isExportationAuthorized, isAdditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);

  // Personnels Redux state
  const dispatch = useDispatch();

  // Yetki ve Hata Kontrolleri
  if (isSuccess === false) return redirectToPages(type);
  
  /** Belli Bir Personel Listesini Excele Aktarır */
  const exportPersonnelsToExcel = () => {
    dispatch(actions.getAllPersonnels(personnelsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const widths = [20, 30, 30, 30, 15];
      const columns = ["TC Kimlik No", "Adı", "Soyadı", "Personel Türü"];
      
      entities.forEach(entity => rows.push([
        entity.tcKimlikNo,
        entity.name,
        entity.surname,
        entity.personnelType
      ]));
      
      const filename = personnelsUIProps.queryParams.term + " - " + (personnelsUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Personel Listesi";
      const sheetname = "Personel Listesi";
      exportToExcel({ columns, rows, widths, filename, sheetname });
    });
  }

  /** Belli Bir Personel Listesini PDF e Aktarır */
  const exportPersonnelsToPDF = () => {
    dispatch(actions.getAllPersonnels(personnelsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const views = [ 
        {
          header: "TC Kimlik No",
          rows: entities.map(entity => entity.tcKimlikNo)
        },
        {
          header: "Adı",
          rows: entities.map(entity => entity.name)
        },
        {
          header: "Soyadı",
          rows: entities.map(entity => entity.surname)
        },
        {
          header: "Personel Türü",
          rows: entities.map(entity => entity.personnelType)
        }
      ];
  
      const filename = personnelsUIProps.queryParams.term + " - " + (personnelsUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Personel Listesi";
      exportToPDF(views, filename);
    });
  }

  /** Belli Bir Personel Listesini Yazdırma Penceresini Açar */
  const printPersonnels = () => {
    dispatch(actions.getAllPersonnels(personnelsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const data = [];    
      const header = personnelsUIProps.queryParams.term + " - " + (personnelsUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Personel Listesi";
      const properties = ["TC Kimlik No", "Adı", "Soyadı", "Personel Türü"];
  
      entities.forEach(entity => {
        data.push({
          "TC Kimlik No": entity.tcKimlikNo,
          "Adı": entity.name,
          "Soyadı": entity.surname,
          "Personel Türü": entity.personnelType
        });
      });
  
      print({ data, properties, header });
    });    
  }

  /** Belli Bir Personel Listesini Panoya Kopyalar */
  const copyPersonnels = () => {
    dispatch(actions.getAllPersonnels(personnelsUIProps.queryParams)).then(entities => {
      if (entities.isSuccess === false) return;
      const rows = [];
      const columns = ["TC Kimlik No", "Adı", "Soyadı", "Personel Türü"];
      const header = personnelsUIProps.queryParams.term + " - " + (personnelsUIProps.queryParams.term + 1) + " Eğitim Öğretim Yılı Personel Listesi";
      entities.forEach(entity => rows.push([
        entity.tcKimlikNo,
        entity.name,
        entity.surname,
        entity.personnelType
      ]));
      copy({ header, columns, rows });
    });
  }

  /** Belli Bir Personel Listesinin Sözleşmelerini Yeniler */
  const renewContracts = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
  
    if (ids.length < 1) {
      noRecordsAlert();
      return;
    }
  
    dispatch(actions.renewContracts(ids)).then(result => onResult(result));
  }

  /** Belli Bir Personel Listesinin Sözleşmelerini Siler */
  const deleteContracts = (term, ids) => {
    if (typeof ids === 'string') ids = ids.split(",");
  
    if (ids.length < 1) {
      noRecordsAlert();
      return;
    }

    deletionAlert().then(result => {
      if (result.isConfirmed) dispatch(actions.deleteContracts(term, ids)).then(result => onResult(result));
    });
  }
  
  /** Belli Bir Personel Listesini Siler */
  const deletePersonnels = (ids) => {
    if (typeof ids === 'string') ids = ids.split(",");  

    if (ids.length < 1) {
      noRecordsAlert();
      return;
    }

    deletionAlert().then(result => {
      if (result.isConfirmed) dispatch(actions.deletePersonnels(ids)).then(result => onResult(result));
    });
  }

  const onResult = ({ isSuccess, type, message }) => {
    resultAlert(type, message);
  
    if (isSuccess) {
      setTimeout(() => {
        personnelsUIProps.setIds([]);
        dispatch(actions.getPersonnels(personnelsUIProps.queryParams));
      }, 2500);
    }
  };
  
  const title = <>Personel Listesi{personnelsUIProps.ids.length > 0 ?
                  <> [Seçilen Kayıt Sayısı: <span style={{color:"red"}}>{personnelsUIProps.ids.length}</span>]</> :
                  ""}
                </>;

  return (
    <Card>
      {actionsLoading && <ModalProgressBar />}
      <CardHeader title={title}>
        {
          (isExportationAuthorized || isAdditionAuthorized || isDeletionAuthorized) &&
          <CardHeaderToolbar>
            {
              isExportationAuthorized &&
              <ExportButton
                exportToExcel={exportPersonnelsToExcel}
                exportToPDF={exportPersonnelsToPDF}
                print={printPersonnels}
                copy={copyPersonnels}
              />
            }
            {
              isAdditionAuthorized &&
              <SuccessButton
                path="Code/Loading"
                title="Sözleşmeleri Yenile"
                method={() => renewContracts(personnelsUIProps.ids)}
              />
            }
            {
              isDeletionAuthorized &&
              <DeleteButton 
                title="Sözleşmeleri Sil"
                method={() => deleteContracts(personnelsUIProps.queryParams.term, personnelsUIProps.ids)}
              />
            }
            {
              isAdditionAuthorized &&
              <FormButton 
                path="Navigation/Plus"
                title="Yeni Personel"
                method={personnelsUIProps.openNewPersonnelPage}
              />
            }            
            {
              isDeletionAuthorized &&
              <DeleteButton 
                title="Seçilenleri Sil"
                method={() => deletePersonnels(personnelsUIProps.ids)}
              />
            }
          </CardHeaderToolbar>
        }        
      </CardHeader>
      <CardBody>
        <PersonnelsFilter />
        <PersonnelsTable
          renewContracts={renewContracts}
          deleteContracts={deleteContracts}
          deletePersonnels={deletePersonnels}
        />
      </CardBody>
    </Card>
  );
}